import { Card, ETextStyleVariant, SkeletonLine, Text } from '@outdoorsyco/bonfire';
import React from 'react';
import { useIntl } from 'react-intl';

import { ProBadge } from '@/components/common/ProBadge';
import { trackFeatureViewedEvent } from '@/services/analytics/features';
import { EFeatureName, EFeatureSource } from '@/services/analytics/features/types';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import generateTestIdFrom from '@/utility/generateTestIdFrom';
import { isAutoRentalTile, TAutoRentalTile } from '@/utility/mapAutoSearchResultToTile';
import { isCampgroundTile, ISearchResultTile } from '@/utility/mapSearchResultToTile';
import { formatMeasurement } from '@/utility/measurements';
import { mapPluralUnitToSingular } from '@/utility/units';

import { ListingTilePrice } from './ListingTilePrice';
import { ListingTileRating } from './ListingTileRating';
import { ListingTileTotalPrice } from './ListingTileTotalPrice';
import { computeCustomListingTitle } from './utils';

type TListingTileContentProps = {
  listingTile: ISearchResultTile | TAutoRentalTile;
  showProBadge?: boolean;
  forceShowTotalPrice?: boolean;
  customTitle?: string;
  secondaryInfo?: React.ReactNode;
};

export const ListingTileContent = ({
  listingTile,
  showProBadge,
  forceShowTotalPrice,
  customTitle,
  secondaryInfo,
}: TListingTileContentProps) => {
  const intl = useIntl();

  const showTotalPrice =
    useExperimentIsEnabled(OptimizelyFlags.PRICE_TOTAL_ON_SRP) || forceShowTotalPrice;

  const secondaryInfo1stLineItems = (() => {
    if (!isAutoRentalTile(listingTile) && isCampgroundTile(listingTile)) {
      return [listingTile.sitesDisplayText];
    }

    if (!isAutoRentalTile(listingTile) && listingTile.isStay) {
      return [
        listingTile.details?.classText,
        listingTile.details?.sleeps,
        listingTile.details?.beds,
        listingTile.details?.maxVehicleLength,
      ];
    }

    return [
      listingTile.details?.location,
      listingTile.details?.distance &&
        formatMeasurement(listingTile.details.distance, {
          unit: mapPluralUnitToSingular(listingTile.details.distanceUnit || 'miles'),
          long: true,
          formatOptions: {
            notation: 'compact',
            maximumFractionDigits: 1,
          },
        }),
    ];
  })().filter(Boolean);

  const secondaryInfo2ndLineItems = (() => {
    if (isAutoRentalTile(listingTile)) {
      return [];
    }

    if (isCampgroundTile(listingTile) || listingTile.isStay) {
      return [listingTile.details?.location];
    }

    return [
      listingTile.details?.delivery
        ? intl.formatMessage({ defaultMessage: 'Delivery or Pickup', id: '7DTqtZ' })
        : intl.formatMessage({ defaultMessage: 'Pickup', id: '7uP+Kl' }),
    ];
  })().filter(Boolean);

  return (
    <Card.Content className="w-full">
      <div className="flex gap-2">
        <div className="flex-grow">
          {(listingTile.loading && (
            <Text component="div" variant={ETextStyleVariant.MediumBold}>
              <SkeletonLine width="80%" className="bg-gray-200" />
            </Text>
          )) || (
            <Text component="div" variant={ETextStyleVariant.MediumBold} className="line-clamp-2">
              {showProBadge && listingTile.dealer && (
                // Prevent opening the tile when clicking on the badge
                // eslint-disable-next-line
                <span
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className="mr-2 align-middle">
                  <ProBadge
                    width={24}
                    height={24}
                    // Temporarily remove the title to prevent the browser's default tooltip
                    onMouseEnter={e => {
                      const card = e.currentTarget.closest('a');

                      if (card) {
                        card.setAttribute('data-original-title', card?.getAttribute('title') ?? '');
                        card.removeAttribute('title');
                      }

                      trackFeatureViewedEvent({
                        rentalID: listingTile.id,
                        source: EFeatureSource.LISTING_TILE,
                        featureName: EFeatureName.PRO_BADGE,
                      });
                    }}
                    // Restore the title when the badge tooltip is no longer shown
                    onMouseLeave={e => {
                      const card = e.currentTarget.closest('a');

                      if (card) {
                        card.setAttribute('title', card?.getAttribute('data-original-title') ?? '');
                        card.removeAttribute('data-original-title');
                      }
                    }}
                    // Use negative margin so it does not increase the height of the title
                    className="inline-block -my-0.5"
                  />
                </span>
              )}

              {customTitle || computeCustomListingTitle(listingTile) || listingTile.title}
            </Text>
          )}
        </div>

        <div className="flex items-baseline">
          {(listingTile.loading && (
            <Text component="div" variant={ETextStyleVariant.MediumBold}>
              <SkeletonLine width="50px" className="bg-gray-200" />
            </Text>
          )) ||
            (!!listingTile.details?.ratingNumber && (
              <ListingTileRating
                rating={listingTile.details.ratingNumber}
                quantity={listingTile.details.ratingQtd}
              />
            ))}
        </div>
      </div>

      {secondaryInfo || (
        <>
          {(listingTile.loading && (
            <Text component="div" variant={ETextStyleVariant.MediumRegular}>
              <SkeletonLine width="60%" className="bg-gray-200" />
            </Text>
          )) ||
            (secondaryInfo1stLineItems.length > 0 && (
              <Text
                component="div"
                variant={ETextStyleVariant.MediumRegular}
                className="text-gray-50"
                data-testid={generateTestIdFrom(listingTile.title || '', 'listing-info-1st-line')}>
                {secondaryInfo1stLineItems.join(' • ')}
              </Text>
            ))}

          {(listingTile.loading && (
            <Text component="div" variant={ETextStyleVariant.MediumRegular}>
              <SkeletonLine width="40%" className="bg-gray-200" />
            </Text>
          )) ||
            (secondaryInfo2ndLineItems.length > 0 && (
              <Text
                component="div"
                variant={ETextStyleVariant.MediumRegular}
                className="text-gray-50"
                data-testid={generateTestIdFrom(listingTile.title || '', 'listing-info-2nd-line')}>
                {secondaryInfo2ndLineItems.join(' • ')}
              </Text>
            ))}
        </>
      )}

      {(listingTile.loading && (
        <Text component="div" variant={ETextStyleVariant.MediumBold}>
          <SkeletonLine width="50%" className="mt-2 bg-gray-200" />
        </Text>
      )) ||
        (() => {
          if (!listingTile.price) return null;

          if (!isAutoRentalTile(listingTile) && isCampgroundTile(listingTile)) {
            return <ListingTilePrice {...listingTile.price} className="mt-2" />;
          }

          if (showTotalPrice && listingTile.price?.estimatedPriceBreakdown?.total) {
            return <ListingTileTotalPrice listingTile={listingTile} className="mt-2" />;
          }

          return <ListingTilePrice {...listingTile.price} className="mt-2" />;
        })()}
    </Card.Content>
  );
};
